// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowLeft, Building2 } from 'lucide-react'
import { getOrg, listOrgs, suspendOrg, reactivateOrg, ApiError } from '@/lib/api'
import type { OrgDetail, OrgStatus } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogTrigger, DialogContent, DialogDescription } from '@/components/ui/dialog'
import AccessDenied from '@/components/AccessDenied'

type PageState = 'loading' | 'error' | 'forbidden' | 'not-found' | 'loaded'

function orgStatusVariant(status: OrgStatus) {
  switch (status) {
    case 'active':    return 'active' as const
    case 'suspended': return 'suspended' as const
    case 'archived':  return 'archived' as const
  }
}

function SkeletonField() {
  return <div className="h-4 w-48 rounded shimmer" />
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 py-3 border-b border-border last:border-0">
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wider sm:w-44 shrink-0">
        {label}
      </dt>
      <dd className="text-sm text-foreground font-mono break-all">{children}</dd>
    </div>
  )
}

interface ConfirmDialogProps {
  title: string
  body: string
  actionLabel: string
  actionVariant?: 'destructive-outline' | 'default'
  pending: boolean
  onConfirm: () => Promise<void>
  trigger: React.ReactNode
}

function ConfirmDialog({
  title,
  body,
  actionLabel,
  actionVariant = 'default',
  pending,
  onConfirm,
  trigger,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  async function handleConfirm() {
    await onConfirm()
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent title={title}>
        <DialogDescription className="mb-6">{body}</DialogDescription>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={pending}>
            {t('orgDetail.cancel')}
          </Button>
          <Button variant={actionVariant} size="sm" onClick={() => void handleConfirm()} disabled={pending}>
            {actionLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default function OrgDetailPage() {
  const { t } = useTranslation()
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()

  const [org, setOrg] = useState<OrgDetail | null>(null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [actionPending, setActionPending] = useState(false)
  const [actionError, setActionError] = useState('')

  const fetchOrg = useCallback(async () => {
    if (!orgId) {
      setPageState('not-found')
      return
    }

    setPageState('loading')
    setErrorMsg('')

    try {
      const detail = await getOrg(orgId)
      setOrg(detail)
      setPageState('loaded')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPageState('forbidden')
        return
      }

      // For 404 or any unknown/0 status (endpoint not yet implemented),
      // fall back to listOrgs() and find the org by id to build a partial OrgDetail.
      const shouldFallback =
        err instanceof ApiError && (err.status === 404 || err.status === 0 || err.status >= 500)

      if (shouldFallback) {
        try {
          const orgs = await listOrgs()
          const summary = orgs.find(o => o.id === orgId)
          if (summary) {
            // Build partial OrgDetail from OrgSummary
            setOrg({ ...summary })
            setPageState('loaded')
          } else {
            setPageState('not-found')
          }
        } catch {
          setPageState('not-found')
        }
        return
      }

      setErrorMsg(err instanceof Error ? err.message : String(err))
      setPageState('error')
    }
  }, [orgId])

  useEffect(() => { void fetchOrg() }, [fetchOrg])

  async function handleSuspend(): Promise<void> {
    if (!orgId) return
    setActionPending(true)
    setActionError('')
    try {
      const updated = await suspendOrg(orgId)
      setOrg(updated)
    } catch {
      setActionError(t('orgDetail.actionError'))
    } finally {
      setActionPending(false)
    }
  }

  async function handleReactivate(): Promise<void> {
    if (!orgId) return
    setActionPending(true)
    setActionError('')
    try {
      const updated = await reactivateOrg(orgId)
      setOrg(updated)
    } catch {
      setActionError(t('orgDetail.actionError'))
    } finally {
      setActionPending(false)
    }
  }

  if (pageState === 'forbidden') {
    return <AccessDenied />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Back nav */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/orgs')}
          className="text-muted-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('orgDetail.backToOrgs')}
        </Button>
      </div>

      {/* Loading skeleton */}
      {pageState === 'loading' && (
        <div className="flex flex-col gap-4">
          <div className="h-8 w-64 rounded shimmer" />
          <div className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonField key={i} />
            ))}
          </div>
        </div>
      )}

      {/* Error state */}
      {pageState === 'error' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('orgDetail.loadError')}</p>
          {errorMsg && <p className="text-xs text-muted-foreground max-w-xs">{errorMsg}</p>}
          <Button variant="outline" size="sm" onClick={() => void fetchOrg()} className="mt-1">
            {t('orgDetail.retry')}
          </Button>
        </div>
      )}

      {/* Not-found state */}
      {pageState === 'not-found' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Building2 className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('orgDetail.notFound')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{t('orgDetail.notFoundBody')}</p>
          <Button size="sm" onClick={() => navigate('/orgs')} className="mt-1">
            {t('orgDetail.backToOrgs')}
          </Button>
        </div>
      )}

      {/* Loaded state */}
      {pageState === 'loaded' && org && (
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
            <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
            <div className="relative z-10 px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                  <Building2 className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white leading-tight">{org.displayName}</h1>
                  <p className="text-white/65 text-sm mt-1 font-mono">{org.slug}</p>
                </div>
              </div>
              <Badge variant={orgStatusVariant(org.status)} className="self-start sm:self-auto">
                {t(`orgDetail.status_${org.status}`)}
              </Badge>
            </div>
          </div>

          {/* Details card */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <dl>
              <DetailRow label={t('orgDetail.orgId')}>{org.id}</DetailRow>
              <DetailRow label={t('orgDetail.tenantId')}>
                {org.firebaseTenantId ?? '—'}
              </DetailRow>
              <DetailRow label={t('orgDetail.domains')}>
                {org.domains && org.domains.length > 0 ? (
                  <span className="flex flex-wrap gap-1.5">
                    {org.domains.map(d => (
                      <span
                        key={d}
                        className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-mono text-foreground border border-border"
                      >
                        {d}
                      </span>
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label={t('orgDetail.memberCount')}>
                {org.memberCount != null ? String(org.memberCount) : '—'}
              </DetailRow>
              <DetailRow label={t('orgDetail.status')}>
                <Badge variant={orgStatusVariant(org.status)}>
                  {t(`orgDetail.status_${org.status}`)}
                </Badge>
              </DetailRow>
            </dl>
          </div>

          {/* Suspend / Reactivate */}
          {org.status !== 'archived' && (
            <div className="flex flex-col gap-2">
              {org.status === 'active' ? (
                <ConfirmDialog
                  title={t('orgDetail.suspendConfirmTitle')}
                  body={t('orgDetail.suspendConfirmBody')}
                  actionLabel={t('orgDetail.suspendConfirmAction')}
                  actionVariant="destructive-outline"
                  pending={actionPending}
                  onConfirm={handleSuspend}
                  trigger={
                    <Button variant="destructive-outline" disabled={actionPending}>
                      {t('orgDetail.suspend')}
                    </Button>
                  }
                />
              ) : (
                <ConfirmDialog
                  title={t('orgDetail.reactivateConfirmTitle')}
                  body={t('orgDetail.reactivateConfirmBody')}
                  actionLabel={t('orgDetail.reactivateConfirmAction')}
                  actionVariant="default"
                  pending={actionPending}
                  onConfirm={handleReactivate}
                  trigger={
                    <Button disabled={actionPending}>
                      {t('orgDetail.reactivate')}
                    </Button>
                  }
                />
              )}
              {actionError && (
                <p className="text-xs text-destructive">{actionError}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
